import moment from 'moment-timezone';
import { cancelled_status } from '../../../../constants';
import {
  IST,
  KIND_CANCEL,
  cancel_initiator,
  covers_care,
  has_errors,
  needs_cancel_capture,
  teacher_note_of,
  validate_care_draft
} from '../../../util_components/care_request/care_request_rules';
import {
  is_valid_reason,
  trim_reason
} from '../../../util_components/required_reason_textarea/reason_rules';
import {
  derive_cancel_bucket,
  is_requested_at_in_future,
  requested_at_to_iso
} from '../../../util_components/teacher_cancel_bucket_picker/bucket_rules';
import { resolve_edit_reason } from './edit_reason_rules';

// Pure rules behind the session page's Edit, Change teacher and Change time modals; the components only do I/O.

const NO_EDIT_ERRORS = {
  modal_error: false,
  rating_error: false,
  penalty_reason_error: false,
  status_reason_error: false,
  cancel_bucket_error: false,
  save_modal_error: ''
};

// The Edit modal opens on the loaded session's values with both cancel flags off, so an abandoned edit never leaks.
export const edit_form_from = (session_details) => ({
  ...NO_EDIT_ERRORS,
  status: session_details.session_status,
  teacher_penalty: session_details.teacher_penalty,
  teacher_penalty_reason: session_details.teacher_penalty_reason,
  cancelled_by_student: false,
  cancelled_by_teacher: false,
  status_change_reason: '',
  teacher_cancel_requested_at: '',
  cancel_initiator_error: false
});

// Any typed field clears the save errors; leaving CANCELLED drops who cancelled and the teacher's request time.
export const edit_field_patch = (name, value) => ({
  ...(name === 'status' && value !== 'CANCELLED'
    ? { cancelled_by_student: false, cancelled_by_teacher: false, teacher_cancel_requested_at: '' }
    : {}),
  [name]: value,
  ...NO_EDIT_ERRORS
});

export const ignore_reason_patch = () => ({ ...NO_EDIT_ERRORS });

// The two cancel radios are exclusive; picking one cancels the session. The request time only applies to the teacher.
export function toggle_cancel_patch(form, option) {
  const cleared = {
    cancel_initiator_error: false,
    modal_error: false,
    rating_error: false,
    penalty_reason_error: false,
    cancel_bucket_error: false
  };
  if (option === 'cancelled_by_student') {
    const next_by_teacher = form.cancelled_by_student;
    return {
      cancelled_by_student: !form.cancelled_by_student,
      cancelled_by_teacher: next_by_teacher,
      ...cleared,
      ...(next_by_teacher ? {} : { teacher_cancel_requested_at: '' }),
      status: form.cancelled_by_student ? form.status : 'CANCELLED'
    };
  }
  if (option === 'cancelled_by_teacher') {
    const next_by_teacher = !form.cancelled_by_teacher;
    return {
      cancelled_by_teacher: next_by_teacher,
      cancelled_by_student: form.cancelled_by_teacher,
      ...cleared,
      ...(next_by_teacher ? {} : { teacher_cancel_requested_at: '' }),
      status: form.cancelled_by_teacher ? form.status : 'CANCELLED'
    };
  }
  return {};
}

// Cancelling a live 1-on-1 records Care's "who asked, and when".
export const is_cancel_capture = (status, session_details, care_draft) =>
  !!care_draft &&
  needs_cancel_capture({
    status,
    original_was_cancelled: cancelled_status.includes(session_details.session_status),
    session_type: session_details.session_type
  });

export const edit_reason_of = (form, session_details) =>
  resolve_edit_reason({
    status: form.status,
    original_status: session_details.session_status,
    cancelled_by_student: form.cancelled_by_student,
    cancelled_by_teacher: form.cancelled_by_teacher,
    original_cancelled_by_student: session_details.cancelled_by_student,
    original_cancelled_by_teacher: session_details.cancelled_by_teacher,
    teacher_penalty: form.teacher_penalty,
    original_teacher_penalty: session_details.teacher_penalty,
    teacher_penalty_reason: form.teacher_penalty_reason,
    status_change_reason: form.status_change_reason
  });

// A teacher cancel needs the request time; goserver derives the category from it. Capture mode's Care
// draft already rejects a future time, so only the picker's own field is checked for that here.
export function teacher_cancel_check({
  status,
  cancelled_by_teacher,
  epoch_start_time,
  requested_local,
  capture = false,
  now_ms
}) {
  const bucket = cancelled_by_teacher
    ? derive_cancel_bucket(epoch_start_time, requested_local, IST)
    : '';
  const missing =
    status === 'CANCELLED' &&
    cancelled_by_teacher &&
    (!bucket || (!capture && is_requested_at_in_future(requested_local, IST, now_ms)));
  return { bucket, missing };
}

// Mirrors goserver carereq cancelledStatuses: the statuses a Care cancellation record can belong to.
const CARE_CANCELLED_STATUSES = [
  'CANCELLED',
  'CANCELLED_BY_TEACHER',
  'CANCELLED_BY_ADMIN',
  'CANCELLED_BY_STUDENT',
  'NO_SHOW_CANCELLATION_1HOUR',
  'JOINED_BUT_CANCELLED'
];

export const CARE_OWNS_CANCEL_TIME_NOTE =
  "Pay uses the request time on this cancellation's Care record. To correct it, edit 'Requested at' on the Cancellation under Change requests.";

// Only a session already cancelled by the teacher can have its request time re-saved from Edit.
export const may_have_care_cancel_time = (session_details) =>
  parseInt(session_details.cancelled_by_teacher, 10) === 1 &&
  CARE_CANCELLED_STATUSES.includes(session_details.session_status) &&
  covers_care(KIND_CANCEL, session_details.session_type);

// The Care record made for the session's current cancellation (same second), or null; goserver prices its
// request time over the one Edit stores, as in factsmap careCancel.
export function current_care_cancel_record(care_view, session_status) {
  if (!care_view || !CARE_CANCELLED_STATUSES.includes(session_status)) return null;
  const change = (care_view.changes || []).find(
    (c) => c.change_kind === KIND_CANCEL && c.record_uuid
  );
  const record = change && (care_view.records || []).find((r) => r.uuid === change.record_uuid);
  if (!record) return null;
  const apart_ms = Math.abs(
    moment(record.change_made_at).valueOf() - moment(change.change_made_at).valueOf()
  );
  return apart_ms < 1000 ? record : null;
}

// Re-saving a teacher cancel whose time a current Care record sets: Edit neither asks for nor sends a time.
export const care_owns_cancel_time = (form, session_details, care_record) =>
  !!care_record &&
  form.status === 'CANCELLED' &&
  !!form.cancelled_by_teacher &&
  parseInt(session_details.cancelled_by_teacher, 10) === 1;

// The Edit save, decided: either the state patch that shows the first problem, or what the save sends.
export function plan_edit_save({
  form,
  session_details,
  care_draft,
  ignore_rating,
  ignore_rating_reason,
  now_ms,
  care_record = null
}) {
  const p = parseInt(form.teacher_penalty, 10);
  const edit = edit_reason_of(form, session_details);
  const capture = is_cancel_capture(form.status, session_details, care_draft);
  const care_owns_time = care_owns_cancel_time(form, session_details, care_record);
  const care_errors = capture
    ? validate_care_draft(care_draft, { now_ms, require_initiated_by: false })
    : {};
  const initiator = cancel_initiator(form.cancelled_by_teacher, form.cancelled_by_student);
  const requested_local = capture ? care_draft.requested_at : form.teacher_cancel_requested_at;
  const { missing } = teacher_cancel_check({
    status: form.status,
    cancelled_by_teacher: form.cancelled_by_teacher && !care_owns_time,
    epoch_start_time: session_details.epoch_start_time,
    requested_local,
    capture,
    now_ms
  });
  if (capture && (!initiator || has_errors(care_errors))) {
    return {
      errors: { cancel_initiator_error: !initiator, cancel_bucket_error: missing },
      care_errors
    };
  }
  if (p < 0) return { errors: { modal_error: true } };
  if (ignore_rating && !ignore_rating_reason) return { errors: { rating_error: true } };
  if (edit.penalty_changed && !trim_reason(form.teacher_penalty_reason)) {
    return { errors: { penalty_reason_error: true } };
  }
  if (!edit.reason_valid) return { errors: { status_reason_error: true } };
  if (missing) return { errors: { cancel_bucket_error: true } };
  return { p, edit, capture, initiator, requested_local, care_owns_time };
}

export function update_session_payload({
  session_uuid,
  student_uuid,
  form,
  plan,
  ignore_rating,
  ignore_rating_reason
}) {
  const payload = {
    session_uuid,
    teacher_penalty: plan.p,
    status: form.status,
    teacher_penalty_reason: form.teacher_penalty_reason,
    student_uuid
  };
  if (plan.edit.reason_required) payload.reason = plan.edit.reason;
  if (form.status === 'CANCELLED') payload.cancelled_by_admin = 1;
  if (ignore_rating) {
    payload.ignore_rating = 1;
    payload.ignore_rating_reason = ignore_rating_reason.slice(0, 255);
  }
  if (form.cancelled_by_student) {
    payload.cancelled_by_student = 1;
    payload.cancelled_by_admin = 1;
    payload.cancelled_by_teacher = 0;
  }
  if (form.cancelled_by_teacher) {
    payload.cancelled_by_teacher = 1;
    payload.cancelled_by_admin = 1;
    payload.cancelled_by_student = 0;
    if (!plan.care_owns_time) {
      payload.teacher_cancel_requested_at = requested_at_to_iso(plan.requested_local, IST);
    }
  }
  return payload;
}

// update_session refusals carry the actionable text (e.g. the slot is taken) in reason, exception_message or message.
export const update_session_error_text = (e, fallback) => {
  const d = e?.response?.data;
  return (
    (typeof d?.reason === 'string' && d.reason) ||
    (typeof d?.exception_message === 'string' && d.exception_message) ||
    (typeof d?.message === 'string' && d.message) ||
    fallback
  );
};

export const teacher_options_of = (teachers) =>
  teachers.map((teacher) => ({
    value: teacher.teacher_uuid,
    label: `${teacher.name} ${
      teacher.is_consultant_therapist_in_preferred_group == 1 ? ' (preferred consultant)' : ''
    }`
  }));

// Change teacher: the internal note (apis wants 3+ chars), then Care's details, then a teacher.
export function plan_teacher_change({ change_reason, care_draft, teacher_uuid, now_ms }) {
  const reason = trim_reason(change_reason);
  if (!is_valid_reason(reason)) return { reason_error: true };
  const care_errors = care_draft ? validate_care_draft(care_draft, { now_ms }) : {};
  if (has_errors(care_errors)) return { care_errors };
  if (!teacher_uuid) return { teacher_missing: true };
  return { reason };
}

// The teacher email shows only the note for the teacher, never the internal reason.
export function teacher_change_payload({ session_uuid, teacher_uuid, reason, care_draft }) {
  const payload = { session_uuid, teacher_uuid, reason };
  const teacher_note = teacher_note_of(care_draft);
  if (teacher_note) payload.teacher_note = teacher_note;
  return payload;
}

// Change time: the note is required for the payment audit, then Care's details.
export function plan_time_change({ time_change_reason, care_draft, now_ms }) {
  const reason = trim_reason(time_change_reason);
  if (!is_valid_reason(reason)) return { reason_error: true };
  const care_errors = care_draft ? validate_care_draft(care_draft, { now_ms }) : {};
  if (has_errors(care_errors)) return { care_errors };
  return { reason };
}
